import { ArrayHelpers, FieldArray, FormikProps, getIn } from 'formik';
import React, { ReactElement } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { FaPlusCircle, FaTrash } from 'react-icons/fa';

export type ListProps = {
  controlId: string;
  model: object | string | number;
  label?: string;
  headers?: HeaderType[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  disableRemovalOnFirstRow?: boolean;
};

export type HeaderType = {
  label: string;
  required?: boolean;
};

export const List: React.FC<React.PropsWithChildren<ListProps>> = ({
  controlId,
  model,
  label,
  headers,
  className,
  placeholder,
  disabled = false,
  disableRemovalOnFirstRow = false,
  children,
}) => {
  function addElement(callback: any) {
    callback(JSON.parse(JSON.stringify(model)));
  }

  function removeElement(index: number, callback: any) {
    callback(index);
  }

  return (
    <div className={`list${className ? ` ${className}` : ''}`}>
      <FieldArray name={controlId}>
        {({
          form: { values },
          remove,
          push,
        }: {
          form: FormikProps<{
            [controlId: string]: any[];
          }>;
        } & ArrayHelpers) => {
          const elements = getIn(values, controlId) as any[];

          if (!Array.isArray(elements)) {
            return <div style={{ color: 'red' }}>Element pointed by the controlId of the list should be an array!</div>;
          }

          return (
            <>
              {label && (
                <div className="form-row">
                  <Col>
                    <Form.Label>{label}</Form.Label>
                  </Col>
                </div>
              )}
              {headers && elements.length > 0 && (
                <div className="list-row">
                  <Col xs={1} />
                  {headers.map((header, index) => (
                    <Col key={index} className="d-flex justify-content-center">
                      <span className={header.required ? 'fw-bold' : ''}>
                        {header.label} {header.required ? ' *' : ''}
                      </span>
                    </Col>
                  ))}
                  {!disabled && <Col xs={1} />}
                </div>
              )}
              {elements.map((_, index) => {
                const entries: ReactElement<any>[] = [];

                (React.Children.toArray(children) as ReactElement<any>[]).forEach((child, indexEntry) => {
                  let fieldControlId = `${controlId}.${index}`;

                  if (child.props.controlId) {
                    fieldControlId = `${fieldControlId}.${child.props.controlId}`;
                  }

                  entries.push(
                    <Col key={indexEntry}>
                      {React.cloneElement(child, {
                        ...child.props,
                        controlId: fieldControlId,
                      })}
                    </Col>
                  );
                });

                return (
                  <div key={index} className="row">
                    <Col xs={1} className="d-flex align-items-center justify-content-center">
                      <span>{index + 1}</span>
                    </Col>
                    <Col className="d-flex align-items-center justify-content-center">{entries}</Col>
                    <Col xs={1} className="d-flex align-items-center justify-content-center">
                      {!disabled && (disableRemovalOnFirstRow ? index !== 0 : true) && (
                        <FaTrash className="delete-icon" onClick={() => removeElement(index, remove)} />
                      )}
                    </Col>
                  </div>
                );
              })}
              {elements.length === 0 && (
                <div className="form-row">
                  <Col>
                    <p>{placeholder}</p>
                  </Col>
                </div>
              )}
              {!disabled && (
                <div className="list-row">
                  <Col className="text-center">
                    <FaPlusCircle className="add-icon" onClick={() => addElement(push)} />
                  </Col>
                </div>
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};
